import { ByteArray } from '@/models/cmd';
import { BitArray } from '@/models/cmd/BitArray';
import { GameState } from '@/models/games/enums/GameState';
import { LocalState } from '@/models/games/enums/LocalState';
import {
    FishPrawnCrabRecord,
    FishPrawnCrabResult,
} from '@/models/games/fishprawncrab';
import { BetType } from '@/models/games/fishprawncrab/BetType';
import { GameState as FishPrawnCrabGameState } from '@/models/games/fishprawncrab/GameState';
import { SupportedGameResult } from '@/modules/host/models';

export const GLIDisableBetTypes = [
    BetType.FPCBDoubleFishCalabash,
    BetType.FPCBDoubleFishPrawn,
    BetType.FPCBDoubleFishCrab,
    BetType.FPCBDoubleFishTiger,
    BetType.FPCBDoubleFishRooster,
    BetType.FPCBDoubleCalabashPrawn,
    BetType.FPCBDoubleCalabashCrab,
    BetType.FPCBDoubleCalabashTiger,
    BetType.FPCBDoubleCalabashRooster,
    BetType.FPCBDoublePrawnCrab,
    BetType.FPCBDoublePrawnTiger,
    BetType.FPCBDoublePrawnRooster,
    BetType.FPCBDoubleCrabTiger,
    BetType.FPCBDoubleCrabRooster,
    BetType.FPCBDoubleTigerRooster,
];
export const convertResultStringToRecord = (
    currentResult: SupportedGameResult,
    resultString: string,
    FResult: bigint,
    result?: number
): FishPrawnCrabRecord => {
    const arr = resultString.split(',');
    const d1 = parseInt(arr[0]);
    const d2 = parseInt(arr[1]);
    const d3 = parseInt(arr[2]);
    const { GameID, Result, GameCount } = currentResult;
    const updateResult: FishPrawnCrabResult = Object.assign(
        {
            Dice1: d1,
            Dice2: d2,
            Dice3: d3,
            FResult: FResult.toString(),
            GameCount: GameCount,
            GameID: GameID,
            Result: result ?? Result,
        },
        getResultForFResult(BigInt(FResult))
    );
    const resultRecord = convertResultToRecord(updateResult);
    return resultRecord;
};

export const convertResultToRecord = (
    result: FishPrawnCrabResult
): FishPrawnCrabRecord => {
    const [d1, d2, d3] = [result.Dice1, result.Dice2, result.Dice3].sort(
        (a, b) => a - b
    );
    const resultPoint = d1 + d2 + d3;
    const record: FishPrawnCrabRecord = {
        gameID: result.GameID,
        result: result.Result,
        shoes: Math.floor(result.GameCount),
        round: result.GameCount % 10000,
        resultPoint: resultPoint,
        Dice1: d1,
        Dice2: d2,
        Dice3: d3,
        Win: -1,
        Fish: result.Fish,
        // 0 - No Win
        // 1 - Fish appearing 1 time
        // 2 - Fish appearing 2 times
        // 3 - Fish appearing 3 times
        Calabash: result.Calabash,
        // 0 - No Win
        // 1 - Calabash appearing 1 time
        // 2 - Calabash appearing 2 times
        // 3 - Calabash appearing 3 times
        Tiger: result.Tiger,
        // 0 - No Win
        // 1 - Tiger appearing 1 time
        // 2 - Tiger appearing 2 times
        // 3 - Tiger appearing 3 times
        Crab: result.Crab,
        // 0 - No Win
        // 1 - Crab appearing 1 time
        // 2 - Crab appearing 2 times
        // 3 - Crab appearing 3 times
        Prawn: result.Prawn,
        // 0 - No Win
        // 1 - Prawn appearing 1 time
        // 2 - Prawn appearing 2 times
        // 3 - Prawn appearing 3 times
        Rooster: result.Rooster,
        // 0 - No Win
        // 1 - Rooster appearing 1 time
        // 2 - Rooster appearing 2 times
        // 3 - Rooster appearing 3 times
        FishCalabash: result.FishCalabash,
        FishTiger: result.FishTiger,
        CalabashTiger: result.CalabashTiger,
        TigerCrab: result.TigerCrab,
        CrabFish: result.CrabFish,
        CrabCalabash: result.CrabCalabash,
        CrabPrawn: result.CrabPrawn,
        CrabRooster: result.CrabRooster,
        PrawnFish: result.PrawnFish,
        PrawnCalabash: result.PrawnCalabash,
        PrawnTiger: result.PrawnTiger,
        PrawnRooster: result.PrawnRooster,
        RoosterFish: result.RoosterFish,
        RoosterCalabash: result.RoosterCalabash,
        RoosterTiger: result.RoosterTiger,
        AnyTripleColor: result.AnyTripleColor,
        AnyTripleSymbol: result.AnyTripleSymbol,
    };

    return record;
};

export const getResultForFResult = (FResult: bigint) => {
    const ba: ByteArray = new ByteArray();
    ba.writeUint64(FResult);
    const bitArray = new BitArray(ba);
    return {
        Fish: bitArray.readNumber(2),
        // 0 - No Win
        // 1 - Fish appearing 1 time
        // 2 - Fish appearing 2 times
        // 3 - Fish appearing 3 times
        Calabash: bitArray.readNumber(2),
        // 0 - No Win
        // 1 - Calabash appearing 1 time
        // 2 - Calabash appearing 2 times
        // 3 - Calabash appearing 3 times
        Prawn: bitArray.readNumber(2),
        // 0 - No Win
        // 1 - Prawn appearing 1 time
        // 2 - Prawn appearing 2 times
        // 3 - Prawn appearing 3 times
        Crab: bitArray.readNumber(2),
        // 0 - No Win
        // 1 - Crab appearing 1 time
        // 2 - Crab appearing 2 times
        // 3 - Crab appearing 3 times
        Tiger: bitArray.readNumber(2),
        // 0 - No Win
        // 1 - Tiger appearing 1 time
        // 2 - Tiger appearing 2 times
        // 3 - Tiger appearing 3 times
        Rooster: bitArray.readNumber(2),
        // 0 - No Win
        // 1 - Rooster appearing 1 time
        // 2 - Rooster appearing 2 times
        // 3 - Rooster appearing 3 times
        FishCalabash: bitArray.readBoolean(),
        PrawnFish: bitArray.readBoolean(),
        CrabFish: bitArray.readBoolean(),
        FishTiger: bitArray.readBoolean(),
        RoosterFish: bitArray.readBoolean(),

        PrawnCalabash: bitArray.readBoolean(),
        CrabCalabash: bitArray.readBoolean(),
        CalabashTiger: bitArray.readBoolean(),
        RoosterCalabash: bitArray.readBoolean(),

        CrabPrawn: bitArray.readBoolean(),
        PrawnTiger: bitArray.readBoolean(),
        PrawnRooster: bitArray.readBoolean(),

        TigerCrab: bitArray.readBoolean(),
        CrabRooster: bitArray.readBoolean(),
        RoosterTiger: bitArray.readBoolean(),

        AnyTripleColor: bitArray.readBoolean(),
        AnyTripleSymbol: bitArray.readBoolean(),
    };
};

export const getFishPrawnCrabWinSet = (FResult: bigint): Array<BetType> => {
    let betTypeWin: BetType[] = [];
    const result = getResultForFResult(FResult);
    if (result.Fish > 0) betTypeWin.push(BetType.FPCBFish);
    if (result.Calabash > 0) betTypeWin.push(BetType.FPCBCalabash);
    if (result.Tiger > 0) betTypeWin.push(BetType.FPCBTiger);
    if (result.Crab > 0) betTypeWin.push(BetType.FPCBCrab);
    if (result.Prawn > 0) betTypeWin.push(BetType.FPCBPrawn);
    if (result.Rooster > 0) betTypeWin.push(BetType.FPCBRooster);

    if (result.FishCalabash) betTypeWin.push(BetType.FPCBDoubleFishCalabash);
    if (result.FishTiger) betTypeWin.push(BetType.FPCBDoubleFishTiger);
    if (result.CalabashTiger) betTypeWin.push(BetType.FPCBDoubleCalabashTiger);
    if (result.TigerCrab) betTypeWin.push(BetType.FPCBDoubleCrabTiger);
    if (result.CrabFish) betTypeWin.push(BetType.FPCBDoubleFishCrab);
    if (result.CrabCalabash) betTypeWin.push(BetType.FPCBDoubleCalabashCrab);
    if (result.CrabPrawn) betTypeWin.push(BetType.FPCBDoublePrawnCrab);
    if (result.CrabRooster) betTypeWin.push(BetType.FPCBDoubleCrabRooster);
    if (result.PrawnFish) betTypeWin.push(BetType.FPCBDoubleFishPrawn);
    if (result.PrawnCalabash) betTypeWin.push(BetType.FPCBDoubleCalabashPrawn);
    if (result.PrawnTiger) betTypeWin.push(BetType.FPCBDoublePrawnTiger);
    if (result.PrawnRooster) betTypeWin.push(BetType.FPCBDoublePrawnRooster);
    if (result.RoosterFish) betTypeWin.push(BetType.FPCBDoubleFishRooster);
    if (result.RoosterCalabash)
        betTypeWin.push(BetType.FPCBDoubleCalabashRooster);
    if (result.RoosterTiger) betTypeWin.push(BetType.FPCBDoubleTigerRooster);
    if (result.AnyTripleColor) betTypeWin.push(BetType.FPCBAnyTripleColor);
    if (result.AnyTripleSymbol) betTypeWin.push(BetType.FPCBAnyTripleSymbol);

    return betTypeWin;
};

export const getLocalState = (
    isRest: boolean,
    state: number,
    isGameResultReleased: boolean
) => {
    if (isRest) {
        return LocalState.REST;
    }
    switch (state) {
        case GameState.RoundCancel:
            return LocalState.ROUND_CANCEL;
        case GameState.Restart:
            return GameState.Restart;
        case GameState.Idle:
        case GameState.Shuffle:
            return LocalState.IDLE;
        // return LocalState.SHUFFLING;
        case GameState.Betting:
            return LocalState.BETTING;
        case GameState.Started:
        case FishPrawnCrabGameState.WaitResult:
            return LocalState.DEALING;
        case FishPrawnCrabGameState.Fish:
        case FishPrawnCrabGameState.Calabash:
        case FishPrawnCrabGameState.Tiger:
        case FishPrawnCrabGameState.Crab:
        case FishPrawnCrabGameState.Prawn:
        case FishPrawnCrabGameState.Rooster:
            if (isGameResultReleased) {
                return LocalState.RESULT;
            } else {
                return LocalState.DEALING;
            }
    }

    return LocalState.IDLE;
};
